import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { business, images } from "@/data/content";
import HeroCallback from "@/components/sections/HeroCallback";

const headline = ["Cleaner Water.", "Healthier Water Systems.", "Complete Water Care."];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ onQuote }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden aqua-gradient pt-24 pb-14 lg:pt-24 lg:pb-16" data-testid="hero-section">
      <div className="absolute inset-0 hero-grain opacity-70" />
      <div className="mx-auto max-w-7xl px-5 relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex justify-start mb-4 lg:mb-6"
        >
          <a href="#home" aria-label="Pristine Water Systems home">
            <img src={images.logo} alt="Pristine Water Systems" className="h-20 sm:h-24 lg:h-28 w-auto" data-testid="hero-logo" />
          </a>
        </motion.div>
      </div>
      <div className="mx-auto max-w-7xl px-5 grid lg:grid-cols-2 gap-14 items-start relative">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white/70 border border-pristine-aquamid px-4 py-1.5 mb-7"
          >
            <MapPin className="h-3.5 w-3.5 text-pristine-aquadeep" />
            <span className="text-xs font-semibold tracking-wide text-pristine-blue">Water Tank Cleaning Sunshine Coast</span>
          </motion.div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-pristine-navy leading-[1.05]">
            {headline.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-7 text-base sm:text-lg text-pristine-navy/70 leading-relaxed max-w-xl"
          >
            Pristine Water Systems Sunshine Coast helps homeowners protect and improve their water
            through professional tank cleaning, filtration, UV disinfection, water testing and
            complete water system servicing.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Button
              data-testid="hero-quote-cta"
              onClick={onQuote}
              className="rounded-full bg-pristine-blue hover:bg-pristine-bluehover text-white h-14 px-8 text-base font-semibold group transition-transform hover:scale-[1.03]"
            >
              Request a Free Quote
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <a href={business.phoneHref} data-testid="hero-call-cta">
              <Button
                variant="outline"
                className="rounded-full border-2 border-pristine-blue text-pristine-blue hover:bg-pristine-aqua h-14 px-8 text-base font-semibold w-full"
              >
                <Phone className="mr-2 h-5 w-5" /> Call Our Local Team
              </Button>
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-6 text-sm text-pristine-navy/60"
          >
            Locally operated across Noosa, Maroochydore and the Sunshine Coast.
          </motion.p>
        </div>

        {/* Right image with parallax + callback form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(15,58,112,0.18)] aspect-[5/4] lg:aspect-[5/4]">
            <motion.img
              src={images.heroHome}
              alt="Modern Sunshine Coast home with a residential rainwater tank on a bright sunny day"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.7 }}
            className="relative z-10 -mt-10 mx-2 sm:mx-6"
          >
            <HeroCallback />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
